'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/button';
import { Spinner } from '../../../../../components/spinner';
import {
  LabelDefault,
  MetaDefault,
} from '../../../../../components/typography';
import { EntityIcon } from '../../../../icons/EntityIcon';
import { formatCategorySmart } from '../../../../../utils/category-utils';
import { LinkSquare02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { useFindingSearchContext } from '../../../finding-search/ChatFindingSearchProvider';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { toRecord } from '../shared/network-tool-card-utils';
import { unwrapMcpToolOutput } from '../shared/unwrap-mcp-tool-output';

type ExternalAttackerToolOutput = {
  executionId?: string;
  signalId?: string;
  signalTitle?: string;
  signalCategory?: string;
  mode?: 'awaited' | 'detached';
};
type AttackToolArgs = {
  signalId: string;
};

function readStringField(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];

  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function parseAttackArgs(input: unknown): AttackToolArgs | null {
  const record = toRecord(input);

  if (!record) {
    return null;
  }

  const signalId = readStringField(record, 'signalId');

  if (!signalId) {
    return null;
  }

  return { signalId };
}

function parseStructuredOutputRecord(
  output: unknown,
): ExternalAttackerToolOutput | null {
  const record = toRecord(unwrapMcpToolOutput(output));

  if (!record) {
    return null;
  }

  if (
    typeof record.executionId !== 'string' ||
    record.executionId.length === 0
  ) {
    return null;
  }

  return {
    executionId: record.executionId,
    signalId: readStringField(record, 'signalId'),
    signalTitle: readStringField(record, 'signalTitle'),
    signalCategory: readStringField(record, 'signalCategory'),
    mode:
      record.mode === 'awaited' || record.mode === 'detached'
        ? record.mode
        : undefined,
  };
}

function parseExternalAttackerOutput(
  output: unknown,
  input: unknown,
): ExternalAttackerToolOutput | null {
  const parsed = parseStructuredOutputRecord(output);

  if (!parsed) {
    return null;
  }

  const inputArgs = parseAttackArgs(input);

  if (inputArgs && !parsed.signalId) {
    return { ...parsed, signalId: inputArgs.signalId };
  }

  return parsed;
}

function resolveSignalLabel(output: ExternalAttackerToolOutput): string | null {
  if (output.signalTitle?.trim()) {
    return output.signalTitle.trim();
  }

  if (output.signalCategory) {
    return formatCategorySmart(output.signalCategory);
  }

  return null;
}

function ExternalAttackerSignalLink({
  signalId,
  label,
  onOpenObject,
}: {
  signalId: string;
  label: string;
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  const canOpenSignal = Boolean(onOpenObject);

  return (
    <HStack
      gap={1.5}
      alignItems="center"
      py={1}
      px={1}
      borderRadius="sm"
      cursor={canOpenSignal ? 'pointer' : 'default'}
      _hover={canOpenSignal ? { bg: 'bg.subtle' } : undefined}
      onClick={
        canOpenSignal
          ? () =>
              onOpenObject?.({
                type: 'signal',
                id: signalId,
                label,
              })
          : undefined
      }
    >
      <EntityIcon type="signal" appearance="highlighted" density="compact" />
      <MetaDefault color="fg.default" truncate flex={1}>
        {label}
      </MetaDefault>
    </HStack>
  );
}

function ExternalAttackerResolvedSignalLink({
  signalId,
  initialLabel,
  onOpenObject,
}: {
  signalId: string;
  initialLabel?: string | null;
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  const t = useTranslations('designSystem.chat.signalSelector');
  const { onGetSignalById } = useFindingSearchContext();
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(
    initialLabel?.trim() ? initialLabel.trim() : null,
  );
  const [isLoading, setIsLoading] = useState(
    !initialLabel?.trim() && Boolean(onGetSignalById),
  );

  useEffect(() => {
    if (initialLabel?.trim()) {
      setResolvedLabel(initialLabel.trim());
      setIsLoading(false);

      return;
    }

    if (!onGetSignalById) {
      setIsLoading(false);

      return;
    }

    let cancelled = false;
    const loadSignal = async () => {
      setIsLoading(true);

      try {
        const signal = await onGetSignalById(signalId);

        if (!cancelled) {
          setResolvedLabel(signal?.label ?? null);
        }
      } catch {
        if (!cancelled) {
          setResolvedLabel(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSignal();

    return () => {
      cancelled = true;
    };
  }, [signalId, initialLabel, onGetSignalById]);
  if (isLoading) {
    return (
      <HStack gap={2} alignItems="center" py={1}>
        <Spinner size="xs" />
        <MetaDefault color="fg.muted">{t('loading')}</MetaDefault>
      </HStack>
    );
  }

  return (
    <ExternalAttackerSignalLink
      signalId={signalId}
      label={resolvedLabel ?? signalId}
      onOpenObject={onOpenObject}
    />
  );
}

function ExternalAttackerReviewCard({
  title,
  args,
  isRunning,
  headerActions,
  onOpenObject,
}: {
  title: string;
  args: AttackToolArgs;
  isRunning: boolean;
  headerActions?: ReactNode;
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  const t = useTranslations('designSystem.chat.externalAttackerTool');

  return (
    <FlatToolCard
      title={title}
      isRunning={isRunning}
      headerActions={headerActions}
    >
      <LabelDefault color="fg.muted">{t('signalLabel')}</LabelDefault>
      <ExternalAttackerResolvedSignalLink
        signalId={args.signalId}
        onOpenObject={onOpenObject}
      />
    </FlatToolCard>
  );
}

function ExternalAttackerExecutionCard({
  output,
  headerActions,
  onOpenObject,
}: {
  output: ExternalAttackerToolOutput;
  headerActions?: ReactNode;
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  const tExternal = useTranslations('designSystem.chat.externalAttackerTool');
  const tAttacker = useTranslations('designSystem.chat.attackerTool');
  const signalLabel = resolveSignalLabel(output);
  const executionId = output.executionId;

  return (
    <FlatToolCard
      title={tExternal('launchedTitle')}
      headerActions={
        <>
          {headerActions}
          {onOpenObject && executionId ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() =>
                onOpenObject({
                  type: 'agent-execution',
                  id: executionId,
                  label: tAttacker('viewExecution'),
                })
              }
            >
              <HStack gap={1} alignItems="center">
                <MetaDefault>{tAttacker('viewExecution')}</MetaDefault>
                <LinkSquare02Icon size={11} />
              </HStack>
            </Button>
          ) : null}
        </>
      }
    >
      {output.signalId ? (
        <ExternalAttackerResolvedSignalLink
          signalId={output.signalId}
          initialLabel={signalLabel}
          onOpenObject={onOpenObject}
        />
      ) : undefined}
    </FlatToolCard>
  );
}

export function renderExternalAttackerCard(ctx: CardRenderContext): ReactNode {
  const { toolCallData, onOpenObject, toolName } = ctx;
  const { output, status, input } = toolCallData;
  const headerActions = getHITLArtifactHeaderActions(ctx);

  if (status === 'completed') {
    const parsedOutput = parseExternalAttackerOutput(output, input);

    if (!parsedOutput) {
      return undefined;
    }

    return (
      <ExternalAttackerExecutionCard
        output={parsedOutput}
        headerActions={headerActions}
        onOpenObject={onOpenObject}
      />
    );
  }

  if (status !== 'started' && status !== 'in-progress') {
    return undefined;
  }

  const args = parseAttackArgs(input);

  if (!args) {
    return undefined;
  }

  return (
    <ExternalAttackerReviewCard
      title={toolName}
      args={args}
      isRunning={status === 'in-progress'}
      headerActions={headerActions}
      onOpenObject={onOpenObject}
    />
  );
}
